import React from 'react'
import PropTypes from 'prop-types'

import Notice from 'components/Shared/Notice'

import EmptyComponent from './elements/EmptyComponent'
import CarouselDescription from './elements/CarouselDescription'
import ImageList from './elements/ImageList'
import InfoCard from './elements/InfoCard/InfoCard'
import PortalModal from 'components/Shared/PortalModal'
import ExpandedModal from './elements/ExpandedModal'
import { useCarouselAd } from './useCarouselAd'
import { useCarouselWarnings } from './useCarouselWarnings'
import { useCarouselUploadStore } from './useCarouselUploadStore'
import { LanguageSelector } from '../Edit/CoCreate'
import TipDialogueBox from 'components/Shared/TipDialogueBox'
import { useTranslation } from 'react-i18next'

const CarouselAd = ({
  cards = [],
  onChange,
  eventId,
  tc,
  tc_run_id,
  language,
  onLanguageChange,
  isOpen,
  isRunning,
  readonly,
  onUploadingChange,
}) => {
  const { t } = useTranslation()
  const {
    carouselData,
    selectedImageId,
    setSelectedImageId,
    expandedImage,
    setExpandedImage,
    addCard,
    handleUpdateCardField,
    handleRemoveCard,
    handleSelectPreviousCard,
    handleSelectNextCard,
    handleOpenExpandedImage,
    onBack,
    onNext,
    isFirstItem,
    isLastItem,
  } = useCarouselAd({ cards, onChange, tc, tc_run_id })
  const uploadStore = useCarouselUploadStore({ onUploadingChange })

  const { groupWarnings, cardWarningsById } = useCarouselWarnings({ cards: carouselData })
  const groupWarningsMessage = (
    <div>
      {groupWarnings.map((warning) => (
        <div className="mb-1" key={warning}>{warning}</div>
      ))}
    </div>
  )

  // hide group warnings for now
  const showGroupWarnings = false

  // return
  return (
    <div>
      {carouselData.length > 0 ? (
        <>
          {!readonly && showGroupWarnings && <Notice
            element={groupWarningsMessage}
            isVisible={groupWarnings.length > 0}
            className="mb-2"
          />}
        
          <ImageList 
            images={carouselData}
            setImages={onChange}
            selectedImageId={selectedImageId}
            setSelectedImageId={setSelectedImageId}
            addCard={addCard}
            onOpenExpandedImage={handleOpenExpandedImage}
            readonly={readonly}
          />

          {!readonly && <>
            <CarouselDescription cardsCount={carouselData.length} /> 
            
            <TipDialogueBox text={t('Recommendations.campaign.setup.cocreate.description')} />
          </>}

          <div className="autofill_card bg-white">
            <div className="autofill_card__inner p-0">
              <LanguageSelector
                value={language}
                chosenLanguages={[]}
                disabled={readonly}
                onChange={onLanguageChange}
              />
            </div>
          </div>

          <InfoCard 
            images={carouselData} 
            selectedImageId={selectedImageId} 
            setSelectedImageId={setSelectedImageId}
            onChange={onChange}
            onUpdateCardField={handleUpdateCardField}
            onRemoveCard={handleRemoveCard}
            onSelectPreviousCard={handleSelectPreviousCard}
            onSelectNextCard={handleSelectNextCard}
            onOpenExpandedImage={handleOpenExpandedImage}
            cardWarnings={cardWarningsById[selectedImageId] || []}
            eventId={eventId}
            tc={tc}
            tc_run_id={tc_run_id}
            language={language}
            onLanguageChange={onLanguageChange}
            isOpen={isOpen}
            isRunning={isRunning}
            readonly={readonly}
            uploadStore={uploadStore}
          />
        </>
      ) : (
        <EmptyComponent addCard={addCard}/>
      )}

      <PortalModal
        isOpen={!!expandedImage}
        className="full-height"
        onClickOutside={() => setExpandedImage(null)}
        title='Media Preview'
      >
        <ExpandedModal 
          expandedImage={expandedImage} 
          onBack={onBack} 
          onNext={onNext} 
          isFirstItem={isFirstItem} 
          isLastItem={isLastItem} />
      </PortalModal>
    </div>
  )
}

export default CarouselAd

CarouselAd.propTypes = {
  cards: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  tc: PropTypes.string,
  tc_run_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  language: PropTypes.string,
  onLanguageChange: PropTypes.func,
  isOpen: PropTypes.bool,
  isRunning: PropTypes.bool,
  onUploadingChange: PropTypes.func,
}